///
/// EarthBrowser
///
/// Created by Matt Giger on 11/2/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Local mirror of the `radar_forecast` dataset: one row per (product,
/// valid time) holding the newest model run's frame. Products are the
/// RRFS composite reflectivity ("rrfs-refc", 15-minute steps) and the
/// REFS probability of > 40 dBZ ("refs-prob-refc40", hourly).
///

import { Database } from "@/earthbrowser/database/Database"
import { type DatasetFeature } from "@/earthbrowser/dataset-distribution/DatasetLayer"

export const cRadarForecastProductRefc = "rrfs-refc"
export const cRadarForecastProductProb40 = "refs-prob-refc40"

export interface RadarForecastFrameRecord extends DatasetFeature {
	id: string // product:valid_time
	product: string
	run_time: string
	valid_time: string
	forecast_minutes: number
	image_url: string
	source_path: string
	size_bytes: number
	width: number
	height: number
	vmin: number
	vmax: number
	bounds_wgs84: string
	created_at: string
}

const cBatchSize = 500

export class RadarForecastTable {
	public static async create(db: Database): Promise<void> {
		await db.execMany([
			{
				sql: `create table if not exists radar_forecast_frames (
					id text primary key,
					product text not null,
					run_time text not null,
					valid_time text not null,
					forecast_minutes integer not null,
					image_url text not null,
					source_path text not null,
					size_bytes integer not null,
					width integer not null,
					height integer not null,
					vmin real not null,
					vmax real not null,
					bounds_wgs84 text not null,
					created_at text not null
				)`
			},
			{ sql: "create index if not exists idx_radar_forecast_frames_product_valid on radar_forecast_frames(product, valid_time)" },
		])
	}

	public static async replaceAll(rows: RadarForecastFrameRecord[]): Promise<void> {
		await Database.db.exec("delete from radar_forecast_frames", [], "critical")
		await this.upsert(rows)
	}

	public static async upsert(rows: RadarForecastFrameRecord[]): Promise<void> {
		const statement = `insert into radar_forecast_frames (
				id, product, run_time, valid_time, forecast_minutes,
				image_url, source_path, size_bytes,
				width, height, vmin, vmax,
				bounds_wgs84, created_at
			) values (
				?, ?, ?, ?, ?,
				?, ?, ?,
				?, ?, ?, ?,
				?, ?
			)
			on conflict(id) do update set
				product = excluded.product,
				run_time = excluded.run_time,
				valid_time = excluded.valid_time,
				forecast_minutes = excluded.forecast_minutes,
				image_url = excluded.image_url,
				source_path = excluded.source_path,
				size_bytes = excluded.size_bytes,
				width = excluded.width,
				height = excluded.height,
				vmin = excluded.vmin,
				vmax = excluded.vmax,
				bounds_wgs84 = excluded.bounds_wgs84,
				created_at = excluded.created_at`

		for (let i = 0; i < rows.length; i += cBatchSize) {
			const batch = rows.slice(i, i + cBatchSize)
			await Database.db.bulkInsert(statement, batch.map(r => [
				r.id,
				r.product,
				r.run_time,
				r.valid_time,
				r.forecast_minutes,
				r.image_url,
				r.source_path,
				r.size_bytes,
				r.width,
				r.height,
				r.vmin,
				r.vmax,
				r.bounds_wgs84,
				r.created_at,
			]))
		}
	}

	/**
	 * Frames valid at or after `sinceIso`, oldest first. High priority
	 * like the radar read: this is what puts the forecast on screen
	 */
	public static async getValidSince(sinceIso: string, limit = 1000): Promise<RadarForecastFrameRecord[]> {
		const rows = await Database.db.exec(
			`select
				id, product, run_time, valid_time, forecast_minutes,
				image_url, source_path, size_bytes,
				width, height, vmin, vmax,
				bounds_wgs84, created_at
			from radar_forecast_frames
			where valid_time >= ?
			order by product, valid_time asc
			limit ${Math.max(1, Math.min(limit, 5000))}`,
			[sinceIso],
			"high"
		) as RadarForecastFrameRecord[]
		return rows
	}
}
